// Chakra imports
import {
  Box,
  Button,
  Flex,
  Icon,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { apiurl } from "APIURL";
import axios from "axios";
// Custom components
import Card from "components/card/Card";
import LineChart from "components/charts/LineChart";
import { useEffect, useState } from "react";
import { IoCheckmarkCircle } from "react-icons/io5";
import { MdBarChart, MdOutlineCalendarToday } from "react-icons/md";
// Assets
import { RiArrowUpSFill } from "react-icons/ri";
import { lineChartOptionsTotalSpent } from "variables/charts";

export default function TotalSpent(props: { [x: string]: any }) {
  const { ...rest } = props;

  // Chakra Color Mode

  const [data, setData] = useState<{ name: string; data: number[] }[]>([]);
  const [loading, setLoading] = useState(false);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const iconColor = useColorModeValue("brand.500", "white");
  const bgButton = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const bgHover = useColorModeValue(
    { bg: "secondaryGray.400" },
    { bg: "whiteAlpha.50" }
  );
  const bgFocus = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.100" }
  );

  function getMonthIndex(monthString: string): number {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    // Find the index of the month in the months array
    const monthIndex = months.findIndex(
      (month) => month.toLowerCase() === monthString.toLowerCase()
    );

    if (monthIndex === -1) {
      throw new Error("Invalid month string");
    }

    return monthIndex;
  }

  function getDateFromMonthString(monthString: string): number {
    const currentYear = new Date().getFullYear(); // Get the current year
    const monthIndex = getMonthIndex(monthString); // Get the index of the month (e.g., January is 0)

    // Create a new Date object with the year and month
    const date = new Date(currentYear, monthIndex);

    return date.getMonth() + 1;
  }

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      const professional_revenues_list: number[] = [];
      const stagiaire_revenues_list: number[] = [];

      for (const item of lineChartOptionsTotalSpent.xaxis.categories) {
        const res = await axios.get(
          `${apiurl}/stats/monthlyRevenues?month=${getDateFromMonthString(
            item
          )}`
        );

        professional_revenues_list.push(res.data.professional_revenues);
        stagiaire_revenues_list.push(res.data.stagiaire_revenues);
      }

      setData([
        {
          name: "Profit des professionels",
          data: professional_revenues_list,
        },
        {
          name: "Profit des stagiaires",
          data: stagiaire_revenues_list,
        },
      ]);

      setLoading(false);
    };

    getData();
    console.log(data);
  }, []);

  function calculateTotal(month: number) {
    let total = 1 - 1;
    data.forEach((i) => {
      total += i.data[month];
    });
    console.log(total);
    return total;
  }
  function calculatePercentage(previousValue: number, currentValue: number) {
    const difference = currentValue - previousValue;
    const percentage = (difference / previousValue) * 100;
    console.log(percentage);
    return percentage;
  }
  const previousValue = calculateTotal(new Date().getMonth() - 1);
  const percentage = calculatePercentage(
    previousValue,
    calculateTotal(new Date().getMonth())
  );
  return (
    <Card
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      w="100%"
      mb="0px"
      {...rest}
    >
      <Text
        me="auto"
        color={textColor}
        fontSize="xl"
        fontWeight="700"
        lineHeight="100%"
      >
        Revenue mensuel
      </Text>
      <Flex align="center" justify="space-between" w="100%" pe="20px" pt="5px">
        <Button
          bg={boxBg}
          fontSize="sm"
          fontWeight="500"
          color={textColorSecondary}
          borderRadius="7px"
        >
          <Icon
            as={MdOutlineCalendarToday}
            color={textColorSecondary}
            me="4px"
          />
          Ce mois
        </Button>
        <Button
          ms="auto"
          alignItems="center"
          justifyContent="center"
          bg={bgButton}
          _hover={bgHover}
          _focus={bgFocus}
          _active={bgFocus}
          w="37px"
          h="37px"
          lineHeight="100%"
          borderRadius="10px"
          {...rest}
        >
          <Icon as={MdBarChart} color={iconColor} w="24px" h="24px" />
        </Button>
      </Flex>
      <Flex w="100%" flexDirection={{ base: "column", lg: "row" }}>
        <Flex flexDirection="column" me="20px" mt="28px">
          <Text
            color={textColor}
            fontSize="34px"
            textAlign="start"
            fontWeight="700"
            lineHeight="100%"
          >
            {calculateTotal(new Date().getMonth())} DT
          </Text>
          <Flex align="center" mb="20px">
            <Text
              color="secondaryGray.600"
              fontSize="sm"
              fontWeight="500"
              mt="4px"
              me="12px"
            >
              Chiffre d'affaires total dans ce mois
            </Text>
            <Flex align="center">
              <Icon as={RiArrowUpSFill} color="green.500" me="2px" mt="2px" />
              <Text
                color={percentage >= 0 ? "green.500" : "red.400"}
                fontSize="sm"
                fontWeight="700"
              >
                {percentage.toString().slice(0, 5)}% par rapport au mois
                précédent
              </Text>
            </Flex>
          </Flex>
          <Flex align="center">
            <Icon as={IoCheckmarkCircle} color="green.500" me="4px" />
            <Text
              color={percentage >= 0 ? "green.500" : "red.400"}
              fontSize="md"
              fontWeight="700"
            >
              {percentage >= 0 ? "En piste" : "En décroissance"}
            </Text>
          </Flex>
        </Flex>
        <Box minH="260px" minW="75%" mt="auto">
          {loading === true ? (
            <Spinner />
          ) : (
            <LineChart
              chartData={data}
              chartOptions={lineChartOptionsTotalSpent}
            />
          )}
        </Box>
      </Flex>
    </Card>
  );
}
